/**
 * Survey metadata for dashboards: normalize course config + user preference metadata
 * (from public.user_preferences, via AuthContext) so “Set as Home” can compare apples to apples.
 */
import type { CourseMetadata, HomeSurveyConfigPayload } from '@/types';

type UserMeta = Record<string, unknown> | null | undefined;

export function parseLevelNumber(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string') {
    const m = value.match(/\d+/);
    if (!m) return null;
    const n = Number.parseInt(m[0], 10);
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

export function normalizeInstrumentSlug(value: unknown): string {
  return String(value ?? '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '-');
}

/** Maps Beginner / `level-1` / raw numbers → tier or `level-N` for matching. */
export function normalizeLevelKey(value: unknown): string | null {
  if (value === undefined || value === null) return null;
  const s = String(value).trim().toLowerCase().replace(/\s+/g, '-');
  if (!s.length) return null;
  if (s === 'all' || s.includes('all-level')) return 'all-levels';

  if (s === 'beginner' || s === 'beginners') return 'beginner';
  if (s === 'intermediate') return 'intermediate';
  if (s === 'advanced') return 'advanced';

  const levelSlug = /^level-(\d+)$/.exec(s);
  if (levelSlug) {
    const n = Number.parseInt(levelSlug[1], 10);
    if (!Number.isFinite(n) || n <= 0) return null;
    if (n === 1) return 'beginner';
    if (n === 2) return 'intermediate';
    if (n === 3) return 'advanced';
    return `level-${n}`;
  }

  const n = parseLevelNumber(value);
  if (n !== null) {
    if (n === 1) return 'beginner';
    if (n === 2) return 'intermediate';
    if (n === 3) return 'advanced';
    return `level-${n}`;
  }

  return s;
}

export function parseHomeSurveyConfig(
  metadata: CourseMetadata | null | undefined,
): HomeSurveyConfigPayload | null {
  const raw = metadata?.home_survey_config;
  if (!raw || typeof raw !== 'object') return null;
  const inst = (raw as { instrument?: unknown }).instrument;
  const lev = (raw as { level?: unknown }).level;
  if (typeof inst !== 'string' || !inst.trim()) return null;
  if (lev === undefined || lev === null) return null;
  if (typeof lev !== 'string' && typeof lev !== 'number') return null;
  return { instrument: inst.trim(), level: lev };
}

export function userMatchesHomeSurveyConfig(
  preferenceMetadata: UserMeta,
  config: HomeSurveyConfigPayload,
): boolean {
  const uInst = normalizeInstrumentSlug(preferenceMetadata?.instrument);
  const cInst = normalizeInstrumentSlug(config.instrument);
  if (!uInst || !cInst || uInst !== cInst) return false;
  const uLev = normalizeLevelKey(preferenceMetadata?.level);
  const cLev = normalizeLevelKey(config.level);
  return uLev !== null && cLev !== null && uLev === cLev;
}

export function readHomeSurveyPrefillFromMetadata(
  metadata: UserMeta,
): { instrument: string | null; level: string | null } {
  if (!metadata) return { instrument: null, level: null };

  const instrument = normalizeInstrumentSlug(
    metadata.instrument ?? metadata.primary_instrument ?? metadata.main_instrument,
  );

  const level = normalizeLevelKey(
    metadata.level ?? metadata.user_level ?? metadata.skill_level,
  );

  return {
    instrument: instrument || null,
    level,
  };
}
